import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Users, FileSpreadsheet, MessageCircle, Sparkles, ChevronDown, ChevronRight, Mail, Phone, Calendar } from "lucide-react";
import { AlchamisMark } from "@/components/AlchamisMark";
import { useAuth } from "@/lib/auth-context";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Stat({ label, value, suffix, testId }) {
  return (
    <div className="bg-white border border-platinum-soft p-6" data-testid={testId}>
      <div className="text-[0.65rem] tracking-[0.22em] uppercase text-navy/60">{label}</div>
      <div className="font-display text-4xl text-navy mt-3">
        {value}
        {suffix && <span className="text-base text-navy/50 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Overview", icon: Sparkles },
  { id: "assessments", label: "AI Assessments", icon: FileSpreadsheet },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "contacts", label: "Contacts", icon: Users },
  { id: "demo_requests", label: "Demo Requests", icon: FileSpreadsheet },
  { id: "conversations", label: "Concierge", icon: MessageCircle },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [s, c, d, a, conv, b] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/contacts"),
          api.get("/admin/demo-requests"),
          api.get("/admin/assessments"),
          api.get("/admin/conversations"),
          api.get("/admin/bookings"),
        ]);
        setStats(s.data);
        setContacts(c.data);
        setDemoRequests(d.data);
        setAssessments(a.data);
        setConversations(conv.data);
        setBookings(b.data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleLogout() {
    await logout();
    navigate("/admin/login");
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-platinum text-navy/60">
        <div className="font-display text-lg tracking-widest animate-pulse">Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-platinum text-navy">
      <header className="bg-white border-b border-platinum-soft">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <AlchamisMark size={32} color="#001B54" />
            <div>
              <div className="font-display text-lg tracking-[0.14em] text-navy">ALCHAMIS</div>
              <div className="text-[0.55rem] tracking-[0.32em] text-gold-deep -mt-0.5">INTERNAL PORTAL</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-xs text-navy/60">{user?.email}</div>
              <div className="text-[0.6rem] tracking-[0.22em] uppercase text-gold-deep">{user?.role}</div>
            </div>
            <Link to="/" className="text-xs tracking-[0.22em] uppercase text-navy/60 hover:text-navy">Public site</Link>
            <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 border border-navy/15 text-xs uppercase tracking-[0.18em] hover:border-navy">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="text-[0.65rem] tracking-[0.28em] uppercase text-gold-deep">Welcome, {user?.name?.split(" ")[0] || "Partner"}</div>
            <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2 leading-tight">Engagement Intelligence.</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 border text-[0.7rem] tracking-[0.18em] uppercase transition-all ${
                  tab === t.id ? "bg-navy text-white border-navy" : "bg-white text-navy border-platinum-soft hover:border-navy/50"
                }`}
              >
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "overview" && stats && (
          <div className="mt-10 space-y-8" data-testid="admin-overview">
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <Stat label="New Contacts" value={stats.new_contacts} testId="stat-new-contacts" />
              <Stat label="Total Contacts" value={stats.total_contacts} testId="stat-total-contacts" />
              <Stat label="Demo Requests" value={stats.total_demo_requests} testId="stat-demo-requests" />
              <Stat label="AI Assessments" value={stats.total_assessments} testId="stat-assessments" />
              <Stat label="Briefings Booked" value={stats.total_bookings || 0} testId="stat-bookings" />
              <Stat label="Avg Lead Score" value={stats.avg_lead_score} suffix="/100" testId="stat-lead-score" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 bg-white border border-platinum-soft p-8">
                <div className="text-[0.65rem] tracking-[0.22em] uppercase text-navy/60">Industry Breakdown</div>
                <div className="mt-6 space-y-3">
                  {(stats.industry_breakdown || []).length === 0 && (
                    <div className="text-sm text-navy/50">No assessments yet.</div>
                  )}
                  {(stats.industry_breakdown || []).map((b) => {
                    const max = Math.max(...stats.industry_breakdown.map((x) => x.count));
                    const pct = max > 0 ? (b.count / max) * 100 : 0;
                    return (
                      <div key={b.industry}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-navy/80">{b.industry}</span>
                          <span className="text-navy/60">{b.count}</span>
                        </div>
                        <div className="h-1 bg-platinum-soft relative">
                          <div className="absolute top-0 left-0 h-full bg-gold" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white border border-platinum-soft p-8">
                <div className="text-[0.65rem] tracking-[0.22em] uppercase text-navy/60">Concierge Activity</div>
                <div className="font-display text-5xl text-navy mt-4">{stats.total_concierge_sessions}</div>
                <div className="text-sm text-navy/50 mt-2">private conversations initiated</div>
              </div>
            </div>
          </div>
        )}

        {tab === "assessments" && (
          <div className="mt-10 space-y-3" data-testid="admin-assessments-list">
            {assessments.length === 0 && (
              <div className="bg-white border border-platinum-soft p-12 text-center text-navy/50">No AI assessments yet.</div>
            )}
            {assessments.map((a) => (
              <div key={a.id} className="bg-white border border-platinum-soft">
                <button
                  onClick={() => setOpenId(openId === a.id ? null : a.id)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-platinum-soft/40"
                  data-testid={`assessment-row-${a.id}`}
                >
                  <div className="flex items-center gap-4">
                    {openId === a.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <div>
                      <div className="font-display text-lg text-navy">{a.full_name} <span className="text-navy/40">·</span> {a.company}</div>
                      <div className="text-xs text-navy/55">{a.industry} · {a.company_size} · {a.email}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl gold-text">{a.lead_score || 0}</div>
                    <div className="text-[0.55rem] tracking-[0.22em] uppercase text-navy/40">Lead Score</div>
                  </div>
                </button>
                {openId === a.id && (
                  <div className="border-t border-platinum-soft p-6 grid grid-cols-1 md:grid-cols-3 gap-6 bg-platinum/60">
                    <div className="space-y-2">
                      <div className="font-display text-3xl text-navy">{a.report?.business_maturity_score ?? "—"}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-navy/50">Maturity</div>
                    </div>
                    <div className="space-y-2">
                      <div className="font-display text-3xl text-navy">{a.report?.digital_transformation_score ?? "—"}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-navy/50">Digital</div>
                    </div>
                    <div className="space-y-2">
                      <div className="font-display text-3xl text-navy">{a.report?.ai_readiness_score ?? "—"}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-navy/50">AI Readiness</div>
                    </div>
                    <div className="md:col-span-3 space-y-3">
                      <div className="text-xs uppercase tracking-[0.18em] text-navy/50">Executive Summary</div>
                      <p className="text-navy/85 whitespace-pre-wrap leading-relaxed">{a.report?.executive_summary}</p>
                      {a.report?.recommended_solutions && (
                        <div className="mt-4">
                          <div className="text-xs uppercase tracking-[0.18em] text-navy/50 mb-2">Recommended Platforms</div>
                          <div className="flex flex-wrap gap-2">
                            {a.report.recommended_solutions.map((r, i) => (
                              <span key={i} className="px-3 py-1.5 bg-navy text-white text-xs tracking-wide">{r.name}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-10 bg-white border border-platinum-soft overflow-x-auto" data-testid="admin-bookings-list">
            <table className="w-full">
              <thead className="bg-navy text-white">
                <tr className="text-[0.65rem] tracking-[0.18em] uppercase">
                  <th className="text-left px-5 py-3 font-medium">Name</th>
                  <th className="text-left px-5 py-3 font-medium">Company</th>
                  <th className="text-left px-5 py-3 font-medium">Topic</th>
                  <th className="text-left px-5 py-3 font-medium">Date</th>
                  <th className="text-left px-5 py-3 font-medium">Time</th>
                  <th className="text-left px-5 py-3 font-medium">Source</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-10 text-navy/50">No briefings booked yet.</td></tr>
                )}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-platinum-soft text-sm" data-testid={`booking-row-${b.id}`}>
                    <td className="px-5 py-4 text-navy font-medium">
                      <div>{b.name}</div>
                      <div className="text-xs text-navy/55">{b.email}</div>
                    </td>
                    <td className="px-5 py-4 text-navy/75">{b.company || "—"}</td>
                    <td className="px-5 py-4 text-navy/75">{b.topic || "—"}</td>
                    <td className="px-5 py-4 text-navy/85">{b.preferred_date}</td>
                    <td className="px-5 py-4 text-navy/85">{b.preferred_time}</td>
                    <td className="px-5 py-4 text-navy/55 text-xs uppercase tracking-wider">{b.source || "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2 py-1 text-[0.65rem] tracking-[0.16em] uppercase border ${
                        b.status === "confirmed" ? "border-emerald-500 text-emerald-700" :
                        b.status === "cancelled" ? "border-rose-500 text-rose-700" :
                        b.status === "completed" ? "border-navy text-navy" :
                        "border-gold text-gold-deep"
                      }`}>{b.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "contacts" && (
          <div className="mt-10 bg-white border border-platinum-soft overflow-x-auto" data-testid="admin-contacts-list">
            <table className="w-full">
              <thead className="bg-navy text-white">
                <tr className="text-[0.65rem] tracking-[0.18em] uppercase">
                  <th className="text-left px-5 py-3 font-medium">Name</th>
                  <th className="text-left px-5 py-3 font-medium">Company</th>
                  <th className="text-left px-5 py-3 font-medium">Interest</th>
                  <th className="text-left px-5 py-3 font-medium">Email</th>
                  <th className="text-left px-5 py-3 font-medium">Score</th>
                  <th className="text-left px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {contacts.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-navy/50">No contacts yet.</td></tr>
                )}
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-platinum-soft text-sm" data-testid={`contact-row-${c.id}`}>
                    <td className="px-5 py-4 text-navy font-medium">{c.name}</td>
                    <td className="px-5 py-4 text-navy/75">{c.company || "—"}</td>
                    <td className="px-5 py-4 text-navy/75">{c.interest || "—"}</td>
                    <td className="px-5 py-4 text-navy/75 break-all">{c.email}</td>
                    <td className="px-5 py-4"><span className="font-display text-lg gold-text">{c.lead_score}</span></td>
                    <td className="px-5 py-4 text-navy/75 uppercase tracking-[0.14em] text-[0.7rem]">{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "demo_requests" && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-demos-list">
            {demoRequests.length === 0 && (
              <div className="bg-white border border-platinum-soft p-12 text-center text-navy/50 md:col-span-2">No demo requests yet.</div>
            )}
            {demoRequests.map((d) => (
              <div key={d.id} className="bg-white border border-platinum-soft p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-xl text-navy">{d.name}</div>
                    <div className="text-xs text-navy/55">{d.company || "—"} · {d.role || "—"}</div>
                  </div>
                  <span className="text-[0.65rem] tracking-[0.22em] uppercase bg-navy text-white px-2 py-1">{d.solution}</span>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-navy/75">
                  <div className="flex items-center gap-2"><Mail size={14} className="text-gold-deep" />{d.email}</div>
                  {d.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-gold-deep" />{d.phone}</div>}
                </div>
                {d.message && <p className="mt-4 text-sm text-navy/65 border-t border-platinum-soft pt-3 leading-relaxed">{d.message}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "conversations" && (
          <div className="mt-10 space-y-3" data-testid="admin-conversations-list">
            {conversations.length === 0 && (
              <div className="bg-white border border-platinum-soft p-12 text-center text-navy/50">No concierge conversations yet.</div>
            )}
            {conversations.map((c) => (
              <div key={c.session_id} className="bg-white border border-platinum-soft">
                <button
                  onClick={() => setOpenId(openId === c.session_id ? null : c.session_id)}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-platinum-soft/40"
                  data-testid={`conversation-${c.session_id}`}
                >
                  <div className="flex items-center gap-3">
                    {openId === c.session_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <div>
                      <div className="font-display text-lg text-navy">{c.visitor?.name || "Anonymous Visitor"}</div>
                      <div className="text-xs text-navy/55">{c.visitor?.email || "no email"} · {c.message_count} messages</div>
                    </div>
                  </div>
                  <div className="text-xs text-navy/50">{new Date(c.last_message_at).toLocaleString()}</div>
                </button>
                {openId === c.session_id && (
                  <div className="border-t border-platinum-soft p-6 space-y-3 bg-platinum/60">
                    {c.messages.map((m, i) => (
                      <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`}>
                        <div className="text-[0.6rem] tracking-[0.22em] uppercase text-navy/40 mb-1">
                          {m.role === "user" ? "Visitor" : "Concierge"}
                        </div>
                        <div className={`inline-block px-4 py-3 border max-w-[85%] whitespace-pre-wrap leading-relaxed ${
                          m.role === "user" ? "bg-gold/15 border-gold/40" : "bg-white border-platinum-soft"
                        } text-navy/85`}>{m.content}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
